import React from 'react';

import cn from 'classnames';
import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../init-store';
import { Button } from '../../../../shared/components';
import SvgImage from '../../../../shared/components/svg-image';
import { SELF } from '../../../../shared/constants';
import { selectHideNonCallsRelatedInterface } from '../../../../shared/features/config/selectors';
import useShouldShowModerationControls from '../../../../shared/features/moderation/hooks/useShouldShowModerationControls';
import { getIsVideoShouldBeMirrored } from '../../../../shared/features/settings';
import { selectIsFullscreen, setIsFullscreenAction } from '../../features/fullscreen';
import { getParticipantsCount, isSidebarVisible, selectVideoGrid } from '../../selectors';
import { OutgoingCallStatus } from '../outgoing-call-status';
import { ParticipantAvatar } from '../participant-avatar';
import { ParticipantName } from '../participant-name';
import css from './index.css';
import { ParticipantProps } from './types';
import Video from './video';

const connector = connect(
    (state: RootState) => {
        const { config } = state;
        const videoGrid = selectVideoGrid(state);
        return {
            isFullscreen: selectIsFullscreen(state),
            activeGridView: videoGrid.activeGridView,
            isChatVisible: isSidebarVisible(state),
            participantsCount: getParticipantsCount(state),
            hideParticipantName: config.hideParticipantName,
            isMirrorVideo: getIsVideoShouldBeMirrored(state),
        };
    },
    {
        setIsFullscreenAction,
    }
);

const Participant: React.FC<ParticipantProps & ConnectedProps<typeof connector>> = ({
    id,
    uid,
    style,
    className,
    videoStream,
    isDominant,
    displayName,
    videoTrackState,
    audioTrackState,
    isFromOverlay,
    isSharingScreen,
    isPresenter,
    activeGridView,
    innerWrapperClassName,
    isPresenterMini,
    videoClassName,
    canFullscreen = false,
    isFullscreen,
    isShowNameOnHover,
    setIsFullscreenAction,
    participantsCount,
    isChatVisible,
    hideParticipantName,
    scrollSnapAlign,
    children,
    isOneOnOne,
    isPending,
    isCallee,
    callStatus,
    isMirrorVideo,
}) => {
    const hideNonCallsRelatedInterface = useSelector(selectHideNonCallsRelatedInterface);
    const { shouldShowModerationMicrophone, shouldShowModerationPopupButton } = useShouldShowModerationControls(id);

    const isScreenShower = isPresenter || isSharingScreen;

    const isVideoOn = Boolean(videoTrackState && !videoTrackState.muted);
    const isAudioOn = Boolean(audioTrackState && !audioTrackState.muted);
    const isSelf = id === SELF;
    const isMirror = isSelf && isMirrorVideo;

    const shouldShowModerationControls =
        (shouldShowModerationMicrophone || shouldShowModerationPopupButton) &&
        activeGridView === 'default' &&
        !isFromOverlay &&
        !isPending;

    const [shouldShowMoreButtonPopup, setShouldShowMoreButtonPopup] = React.useState(false);

    const toggleMoreButtonPopup = React.useCallback(
        () => setShouldShowMoreButtonPopup(!shouldShowMoreButtonPopup),
        [shouldShowMoreButtonPopup]
    );

    React.useEffect(() => {
        setShouldShowMoreButtonPopup(false);
    }, [participantsCount, isChatVisible]);

    const toggleFullscreen = React.useCallback(() => {
        setIsFullscreenAction(!isFullscreen);
    }, [isFullscreen, setIsFullscreenAction]);

    const avatarBlockContent =
        (isCallee && isPending && callStatus && (
            <OutgoingCallStatus calleeName={displayName} callStatus={callStatus} />
        )) ||
        (hideParticipantName ? null : (
            <ParticipantName
                className={css.avatarWithNameBlock__name}
                userId={id}
                isAudioOn={isAudioOn}
                activeGridView={activeGridView}
                displayName={displayName}
                isFromOverlay={Boolean(isFromOverlay)}
                toggleMoreButtonPopup={toggleMoreButtonPopup}
                shouldShowMoreButtonPopup={shouldShowMoreButtonPopup}
                shouldShowModerationMicrophone={shouldShowModerationMicrophone}
                shouldShowModerationControls={shouldShowModerationControls}
                shouldShowModerationPopupButton={shouldShowModerationPopupButton}
            />
        ));

    const content = children ? (
        <>
            <div className={cn(css.avatarWrapper, css.avatarBlockWithMargin)}>
                <ParticipantAvatar userId={id} isVideoOn={isVideoOn} uid={uid} small={Boolean(isPresenterMini)} />
            </div>
            <div className={css.ChildrenWrapper}>{children}</div>
        </>
    ) : (
        <div className={cn(css.avatarWithNameBlock, { [css.withoutName]: hideParticipantName })}>
            <ParticipantAvatar
                userId={id}
                className={css.avatarWithNameBlock__avatar}
                isVideoOn={isVideoOn}
                uid={uid}
                small={Boolean(isPresenterMini)}
                isPending={isPending}
            />
            <div className={css.avatarWithNameBlock__content}>{avatarBlockContent}</div>
        </div>
    );

    const fullscreenButtonIcon = React.useMemo(() => {
        return <SvgImage imageName={isFullscreen ? 'fullscreenOut' : 'fullscreenIn'} />;
    }, [isFullscreen]);

    return (
        <div
            className={cn(css.Participant, className, {
                [css.Participant_dominant]: !hideNonCallsRelatedInterface && isDominant,
                [css.Participant_fromOverlay]: Boolean(isFromOverlay),
                [css.Participant_presenter]: isScreenShower,
                [css.Participant_presenter_mini]: isPresenterMini,
                [css.Participant_oneOnOne]: isOneOnOne,
                [css.Participant_isVideoOn]: isVideoOn,
                [css.Participant_moderator]: shouldShowModerationControls,
                [css.Participant_moderationPopup]: shouldShowMoreButtonPopup,
                [css.Participant_scrollSnapAlignStart]: scrollSnapAlign === 'start',
                [css.Participant_scrollSnapAlignEnd]: scrollSnapAlign === 'end',
                [css.Participant_scrollSnapAlignCenter]: scrollSnapAlign === 'center',
            })}
            style={style}
        >
            <div
                className={cn(css.Participant__inner, innerWrapperClassName, {
                    [css.Participant__inner_showNameOnHover]: isShowNameOnHover,
                })}
            >
                <div className={css.Participant__videoContainer}>
                    <Video
                        className={cn(css.Participant__video, videoClassName, {
                            [css.Participant__video_mirror]: isMirror,
                            [css.ScreenSharing]: isSharingScreen,
                            [css.isPresenterVideoOn]: isScreenShower && isVideoOn,
                        })}
                        videoStream={videoStream}
                        stretchVideo={!isScreenShower}
                    />
                </div>
                <div className={css.ContentWrapper}>{content}</div>
                {document.fullscreenEnabled && canFullscreen && isSharingScreen && (
                    <Button
                        view="neutral-transparent"
                        size="sm"
                        className={css.fullscreenButton}
                        onClick={toggleFullscreen}
                        icon={fullscreenButtonIcon}
                    />
                )}
            </div>
        </div>
    );
};

export default connector(Participant);
